/**
 * Canvas Manager - Creates and configures the render canvas for the viewer.
 *
 * Supports multiple input types for developer convenience:
 * - CSS selector string (e.g. '#app', '.viewer')
 * - HTMLElement reference (e.g. React ref.current)
 * - null/undefined (falls back to existing #renderCanvas or document.body)
 */

/**
 * Set up the render canvas inside the given container element.
 *
 * @param element - CSS selector, HTMLElement, or null/undefined
 * @returns The created or found HTMLCanvasElement with id="renderCanvas"
 */
export function setupCanvas(element?: string | HTMLElement | null): HTMLCanvasElement {
  // Resolve container
  let container: HTMLElement | null = null;

  if (typeof element === 'string') {
    container = document.querySelector(element);
    if (!container) {
      throw new Error(`XRGallery: No element found for selector "${element}"`);
    }
  } else if (element instanceof HTMLElement) {
    container = element;
  }

  // If no container was provided, look for an existing canvas first
  if (!container) {
    const existing = document.getElementById('renderCanvas') as HTMLCanvasElement | null;
    if (existing) {
      return existing;
    }
    // Fall back to document.body
    container = document.body;
  }

  // Check if the container already has a #renderCanvas inside it
  const existingChild = container.querySelector('#renderCanvas') as HTMLCanvasElement | null;
  if (existingChild) {
    return existingChild;
  }

  // Create a new canvas inside the container
  const canvas = document.createElement('canvas');
  canvas.id = 'renderCanvas';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  canvas.style.outline = 'none';
  container.appendChild(canvas);

  return canvas;
}
